/**
 * web-account-pages D2 / D3 — the website's one live chat per tab, shared by the header's Messages
 * button, the messages panel and the message banner. No React: `WebChatProvider` holds one hub and
 * the pieces read its state.
 *
 * - Feeds: one `RoomFeed` per room query (`secure`, `telegram:<lens>`), so the filters that read
 *   the same query share it (All, Secure and Chat all read the one Secure feed). The All filter's
 *   two feeds are always held while signed in: they carry the count, the arrivals and the banner.
 *   A filter's other feed is loaded when the filter is chosen and refreshed while it is.
 * - The count: `GET /chat/unread` once on start; after that every thread and read answer's
 *   `secureUnread` (design D3, Codex re-review R7). Plus, on the website only (Gabriel 2026-10-23):
 *   each studio notice or reply that reaches the Telegram room while the site is open and Messages
 *   is closed, until Messages opens. Held for this browser tab, so moving between a custom site's
 *   pages keeps it and closing the tab clears it (card 4 walk, Gabriel 2026-11-01); what was already
 *   there when the site opened never counts.
 * - The event stream is invalidation only (D3). It opens once the All feeds' first pages are in,
 *   so the hub knows whether a conversation exists: with none the server never subscribes the
 *   connection, so it counts as down for the fallback poll and is reopened when the conversation
 *   appears (the first send answers `conversationCreated`, or a read shows a sequence). Every event
 *   marks the held feeds stale; one refresh per feed runs (`afterSeq`, plus `ledgerSince` with the
 *   60-second overlap for Telegram), and an event while that feed's refresh is in flight queues one
 *   follow-up, never one per event. A `message` event for a sequence the hub already holds (the
 *   client's own send, absorbed from its answer) refreshes nothing. Reconnects back off 1, 2, 5,
 *   10, 30 seconds; every connection's `ready` refreshes, so what arrived meanwhile shows once. The stream
 *   closes when the tab has been hidden for a minute and reopens when it is shown.
 * - The fallback poll: every 30 seconds while the stream is up and subscribed, every 10 while it
 *   is down or blind; skipped while hidden.
 * - A 429 pauses every refresh, poll and page until its `Retry-After`, then one catch-up runs;
 *   nothing on screen is dropped.
 * - Arrivals: a studio message a refresh of the All feeds brings that the hub never held before.
 *   First pages, catch-up pages and older pages only mark what they bring as held.
 * - Read acknowledgements (D2): per room, only while the panel is open and the page visible, by
 *   `readToAcknowledge` over the stream the chosen filter shows, and never while the screen holds
 *   new messages back (`holdReads`: the phone's rest gate, Codex review 2026-10-26). A failed POST
 *   is retried by the next fetch, never in a loop.
 * - A catch-up cut short (a failed or rate-limited page) resumes with the feed's next refresh, so
 *   the reads it blocks go out once every unread message is in (Codex review 2026-10-26).
 * - Send: the Mini App's rules (useClientChatReplies): a bubble at once under a new idempotency key,
 *   the words kept for this browser session until the server answers, a lost answer re-sent under
 *   the same key, a refused one shown as not sent and forgotten. A failure line waits for the
 *   send's landing to settle (lib/chat/landingHold), as the app's does.
 * - A first page answered 404 (the person has no chat with the studio) is not found, not failed:
 *   the panel says the chat is not available and offers no Try again (shared-client-screens M10).
 */
import type { ChatTypingEvent, ClientChatSendBody, ClientThreadEntry, ClientThreadQuery, ThreadRoom } from '@telify/shared';
import { forgetUnsent, readUnsent, rememberUnsent, type UnsentReply } from '@/lib/chat/chatDraft';
import {
  LEDGER_OVERLAP_MS,
  POLL_STREAM_DOWN_MS,
  POLL_STREAM_UP_MS,
  STREAM_BACKOFF_MS,
  TYPING_POST_INTERVAL_MS,
  typingLineMs,
} from '@/lib/chat/chatTimings';
import { expectLanding, holdUntilLanded } from '@/lib/chat/landingHold';
import { endOlderPage, noteRemovals, startOlderPage, type OlderPageHolder } from '@/lib/chat/olderPageGuard';
import type { ClientChatMessageEntry, ClientSendIssue } from '@/lib/client/clientChatThread';

import type * as chatApi from '../api/chat';
import { retryAfterMs } from '../api/chat';
import { SiteFetchError } from '../api/session';
import { webFilterKeeps, webFilterReads, type WebMessageFilter, type WebRoomQuery } from './messageFilters';
import {
  absorbCatchUpPage,
  absorbFirstPage,
  absorbOlderPage,
  absorbRefresh,
  absorbSent,
  compareStreamItems,
  emptyRoomFeed,
  markAcknowledged,
  mergeStream,
  readToAcknowledge,
  type RoomFeed,
  type StreamItem,
} from './messageStream';

/** The stream stays open this long after the tab is hidden. */
export const HIDDEN_CLOSE_MS = 60_000;

/** The transport the hub runs on (`web/api/chat.ts`); tests stand in for it. */
export interface ChatTransport {
  readonly fetchThread: typeof chatApi.fetchWebThread;
  readonly fetchUnread: typeof chatApi.fetchWebChatUnread;
  readonly markRead: typeof chatApi.markWebChatRead;
  readonly send: typeof chatApi.sendWebMessage;
  readonly postTyping: typeof chatApi.postWebTyping;
  readonly readEvents: typeof chatApi.readWebChatEvents;
}

export type FeedKey = 'secure' | `telegram:${string}`;
export const feedKeyOf = (query: WebRoomQuery): FeedKey => (query.room === 'secure' ? 'secure' : `telegram:${query.filter}`);

/** What a send that did not store shows under its bubble. */
export type SendIssue = ClientSendIssue;

export interface WebChatState {
  /** Signed out: nothing is read. */
  readonly live: boolean;
  readonly feeds: Readonly<Partial<Record<FeedKey, RoomFeed>>>;
  /** Feeds whose first page failed (not a 429 or a 404): the panel offers Try again. */
  readonly failed: ReadonlySet<FeedKey>;
  /** Feeds whose first page answered 404: the chat is not available. */
  readonly notFound: ReadonlySet<FeedKey>;
  /** The studio's secure messages the client has not read, plus its Telegram sends that arrived while Messages was closed: the header's count. */
  readonly unread: number;
  /** The composer's bubbles the server has not stored yet, oldest first. */
  readonly pending: readonly ClientChatMessageEntry[];
  readonly issues: ReadonlyMap<string, SendIssue>;
  /** The studio is typing in the Secure room. */
  readonly typing: boolean;
  readonly loadingOlder: boolean;
}

export interface FilterView {
  readonly status: 'loading' | 'ready' | 'not-found' | 'error';
  /** What the panel draws, oldest first: the shown part of the merge plus unsent bubbles. */
  readonly items: readonly StreamItem[];
  readonly hasOlder: boolean;
}

interface FeedRuntime extends OlderPageHolder {
  loading: boolean;
  busy: boolean;
  again: boolean;
  /** Capped catch-up pages are being fetched. */
  catchingUp: boolean;
}

const BASE_READS = webFilterReads('all');
const BASE_KEYS: readonly FeedKey[] = BASE_READS.map((read) => feedKeyOf(read.query));
const isBase = (key: FeedKey) => BASE_KEYS.includes(key);
const queryOfKey = (key: FeedKey): WebRoomQuery =>
  key === 'secure' ? { room: 'secure' } : { room: 'telegram', filter: key.slice('telegram:'.length) as never };

const IDLE: WebChatState = {
  live: false, feeds: {}, failed: new Set(), notFound: new Set(), unread: 0, pending: [], issues: new Map(), typing: false, loadingOlder: false,
};

const placeholder = ({ key, text, at }: UnsentReply): ClientChatMessageEntry => ({
  id: key, kind: 'chat-message', seq: 0, idempotencyKey: key, channel: 'secure', direction: 'inbound', at, payload: { text },
});

const isNotFound = (error: unknown) => error instanceof SiteFetchError && error.status === 404;

/** A 4xx other than a timeout-like 408 or a rate limit refuses the same request again. */
const isRefusal = (error: unknown) =>
  error instanceof SiteFetchError && error.status >= 400 && error.status < 500 && error.status !== 408 && error.status !== 429;

const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

const seqOf = (entry: ClientThreadEntry) => (entry.kind === 'automatic' ? null : entry.seq);

/** A studio notice or reply in the Telegram room (the secure ping is never one: the room never shows it). */
const isStudioTelegramSend = (entry: ClientThreadEntry) => entry.kind === 'automatic' || entry.kind === 'reply';

/**
 * The Telegram arrivals Messages has not shown, kept for this browser tab: a custom site's every page is a new load, so
 * the count lives through moving between its pages until Messages opens; closing the tab clears it (card 4 walk,
 * Gabriel 2026-11-01). Storage can throw (private mode, quota), so it fails soft to the page's own memory.
 */
const arrivalsKey = (scope: string) => `telify:chat-telegram-arrivals:${scope}`;

function readArrivals(scope: string): Set<string> {
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(arrivalsKey(scope)) ?? '[]');
    return new Set(Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeArrivals(scope: string, ids: ReadonlySet<string>): void {
  try {
    if (ids.size > 0) sessionStorage.setItem(arrivalsKey(scope), JSON.stringify([...ids]));
    else sessionStorage.removeItem(arrivalsKey(scope));
  } catch {
    // Best effort: the count still holds for this page.
  }
}

function parseTyping(data: string): ChatTypingEvent | null {
  try {
    const parsed = JSON.parse(data) as Partial<ChatTypingEvent>;
    if (parsed?.type !== 'typing' || parsed.room !== 'secure' || parsed.from !== 'business' || typeof parsed.until !== 'string') return null;
    return parsed as ChatTypingEvent;
  } catch {
    return null;
  }
}

function newKey(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

export class WebChatHub {
  private state: WebChatState = IDLE;
  private readonly listeners = new Set<() => void>();
  private readonly arrivalListeners = new Set<(items: readonly StreamItem[]) => void>();
  private runtimes = new Map<FeedKey, FeedRuntime>();
  /** Bumped on every start and stop: an answer of an earlier session renders nothing. */
  private generation = 0;
  private userId: string | null = null;
  private filter: WebMessageFilter = 'all';
  private panelOpen = false;
  /** Every entry id the All feeds have held: an arrival is one not in here. */
  private held = new Set<string>();
  private conversationSeen = false;
  private unreadFromReads = false;
  /** The studio's secure messages the client has not read, as the server last counted them. */
  private secureUnread = 0;
  /** Studio Telegram sends that arrived while the site was open and Messages closed (Gabriel 2026-10-23). */
  private telegramArrivals = new Set<string>();
  private ackInFlight = new Set<ThreadRoom>();
  /** True while the screen holds new messages back from view: no read goes out. */
  private readHold: (() => boolean) | null = null;
  private limitedUntil = 0;
  private limitTimer: ReturnType<typeof setTimeout> | null = null;
  // The stream.
  private stream: AbortController | null = null;
  private streamBlind = false;
  private connected = false;
  private attempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private hiddenTimer: ReturnType<typeof setTimeout> | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private pollDue = 0;
  // Typing and sends.
  private typingTimer: ReturnType<typeof setTimeout> | null = null;
  private lastTypingPost = 0;
  private inFlight = new Set<string>();
  private views = new Map<WebMessageFilter, { state: WebChatState; view: FilterView }>();

  constructor(private readonly transport: ChatTransport) {}

  // ── Reading the hub ──

  getState = (): WebChatState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Studio and client entries the All feeds' refreshes brought that were never held before. */
  onArrived(listener: (items: readonly StreamItem[]) => void): () => void {
    this.arrivalListeners.add(listener);
    return () => this.arrivalListeners.delete(listener);
  }

  /** What one filter shows now (memoized per state). */
  viewOf(filter: WebMessageFilter): FilterView {
    const cached = this.views.get(filter);
    if (cached && cached.state === this.state) return cached.view;
    const reads = webFilterReads(filter);
    const feeds = reads.map((read) => this.state.feeds[feedKeyOf(read.query)]);
    const keeps = webFilterKeeps(filter);
    let view: FilterView;
    if (feeds.some((feed) => !feed || !feed.loaded)) {
      const has = (set: ReadonlySet<FeedKey>) => reads.some((read) => set.has(feedKeyOf(read.query)));
      view = { status: has(this.state.notFound) ? 'not-found' : has(this.state.failed) ? 'error' : 'loading', items: [], hasOlder: false };
    } else {
      const merged = mergeStream(feeds as RoomFeed[], keeps);
      const unsent = this.state.pending
        .map((entry): StreamItem => ({ room: 'secure', entry }))
        .filter((item) => keeps(item.room, item.entry));
      const items = unsent.length > 0 ? [...merged.shown, ...unsent].sort(compareStreamItems) : merged.shown;
      view = { status: 'ready', items, hasOlder: merged.hasOlder };
    }
    this.views.set(filter, { state: this.state, view });
    return view;
  }

  // ── Session ──

  /** Signed in as `userId`: read the count and the All feeds, then open the stream. */
  start(userId: string): void {
    if (this.userId === userId && this.state.live) return;
    this.stop();
    const generation = this.generation;
    this.userId = userId;
    const scope = this.scope();
    const restored = readUnsent(scope).map(placeholder);
    this.telegramArrivals = readArrivals(scope);
    this.set({
      ...IDLE,
      live: true,
      pending: restored,
      unread: this.counted(),
      feeds: Object.fromEntries(BASE_KEYS.map((key) => [key, emptyRoomFeed(queryOfKey(key).room)])),
    });
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', this.onVisibility);
    this.transport.fetchUnread().then(
      ({ secureUnread }) => {
        if (generation !== this.generation || this.unreadFromReads) return;
        this.secureUnread = secureUnread;
        this.set({ ...this.state, unread: this.counted() });
      },
      (error) => this.noteLimit(error),
    );
    void Promise.allSettled(BASE_KEYS.map((key) => this.load(key))).then(() => {
      if (generation !== this.generation) return;
      this.openStream();
      this.schedulePoll();
    });
    this.activate(this.filter);
    restored.forEach((entry) => void this.post(entry));
  }

  /** Signed out (or another person): everything stops and nothing of it stays. */
  stop(): void {
    this.generation += 1;
    this.userId = null;
    this.closeStream();
    for (const timer of [this.retryTimer, this.hiddenTimer, this.pollTimer, this.limitTimer, this.typingTimer]) {
      if (timer) clearTimeout(timer);
    }
    this.retryTimer = this.hiddenTimer = this.pollTimer = this.limitTimer = this.typingTimer = null;
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.onVisibility);
    this.runtimes = new Map();
    this.held = new Set();
    this.conversationSeen = false;
    this.unreadFromReads = false;
    this.secureUnread = 0;
    this.telegramArrivals = new Set();
    this.ackInFlight = new Set();
    this.inFlight = new Set();
    this.limitedUntil = 0;
    this.attempt = 0;
    if (this.state !== IDLE) this.set(IDLE);
  }

  // ── The panel ──

  /** The panel opened or closed: acknowledgements go out only while it is open. */
  setPanelOpen(open: boolean): void {
    this.panelOpen = open;
    if (!open) return;
    if (this.telegramArrivals.size > 0) {
      // Opening Messages shows them: they stop counting.
      this.telegramArrivals = new Set();
      writeArrivals(this.scope(), this.telegramArrivals);
      this.set({ ...this.state, unread: this.secureUnread });
    }
    this.acknowledgeShown();
  }

  /** The filter the panel shows: its feeds are read, and refreshed while it is chosen. */
  use(filter: WebMessageFilter): void {
    if (filter === this.filter) return;
    this.filter = filter;
    this.activate(filter);
    this.acknowledgeShown();
  }

  /**
   * The phone's messages hold what arrives at the newest end while the client scrolls older history
   * (its rest gate): while `held()` says so nothing is acknowledged. Null lets go.
   */
  holdReads(held: (() => boolean) | null): void {
    this.readHold = held;
    if (!held) this.acknowledgeShown();
  }

  /** What the screen held back is on screen now: acknowledge it. */
  readsReleased(): void {
    this.acknowledgeShown();
  }

  /** Try again after a failed first page. */
  retryLoad(): void {
    for (const key of this.activeKeys()) if (this.state.failed.has(key)) void this.load(key);
  }

  /** Older history for the chosen filter: each room whose boundary holds the stream back pages once. */
  async loadOlder(): Promise<void> {
    if (this.state.loadingOlder || this.limited()) return;
    const reads = webFilterReads(this.filter);
    const feeds = reads.map((read) => this.state.feeds[feedKeyOf(read.query)]);
    if (feeds.some((feed) => !feed?.loaded)) return;
    const merged = mergeStream(feeds as RoomFeed[], webFilterKeeps(this.filter));
    if (!merged.hasOlder || merged.pageRooms.length === 0) return;
    const generation = this.generation;
    this.set({ ...this.state, loadingOlder: true });
    try {
      await Promise.all(reads.filter((read) => merged.pageRooms.includes(read.room)).map(async (read) => {
        const key = feedKeyOf(read.query);
        const feed = this.state.feeds[key];
        if (!feed?.cursor) return;
        const runtime = this.runtime(key);
        const removed = startOlderPage(runtime);
        try {
          const response = await this.transport.fetchThread({ ...read.query, cursor: feed.cursor });
          const now = this.state.feeds[key];
          if (generation !== this.generation || !now) return;
          this.unreadFrom(response.secureUnread);
          this.setFeed(key, absorbOlderPage(now, response, removed));
          this.hold(key);
        } finally {
          endOlderPage(runtime, removed);
        }
      }));
    } catch (error) {
      this.noteLimit(error);
    } finally {
      if (generation === this.generation) this.set({ ...this.state, loadingOlder: false });
    }
  }

  // ── Sending and typing ──

  /** Shows the bubble at once and posts it. Returns its key (the bubble's id until stored). */
  send(text: string): string {
    const key = newKey();
    const entries = this.state.feeds.secure?.entries ?? [];
    const last = entries.length > 0 ? Date.parse(entries[entries.length - 1].at) : 0;
    // Never above what is already on screen, whatever this device's clock says.
    const unsent: UnsentReply = { key, text, at: new Date(Math.max(Date.now(), last)).toISOString() };
    rememberUnsent(this.scope(), unsent);
    const entry = placeholder(unsent);
    this.set({ ...this.state, pending: [...this.state.pending, entry] });
    expectLanding(key);
    void this.post(entry);
    return key;
  }

  /** Not sent: a click sends the same words under the same key. */
  retrySend(key: string): void {
    if (this.state.issues.get(key) !== 'unsent') return;
    const entry = this.state.pending.find((item) => item.id === key);
    if (entry) void this.post(entry);
  }

  /** A keystroke with words in the pill: the studio sees typing, at most once every 3 seconds. */
  notifyTyping(): void {
    if (!this.state.live) return;
    const now = Date.now();
    if (now - this.lastTypingPost < TYPING_POST_INTERVAL_MS) return;
    this.lastTypingPost = now;
    void this.transport.postTyping();
  }

  // ── Internals: state ──

  private set(next: WebChatState): void {
    this.state = next;
    for (const listener of this.listeners) listener();
    if (this.panelOpen) this.acknowledgeShown();
  }

  private setFeed(key: FeedKey, feed: RoomFeed, extra: Partial<WebChatState> = {}): void {
    let pending = extra.pending ?? this.state.pending;
    if (key === 'secure' && pending.length > 0) {
      // The stored row replaces the bubble: drop bubbles whose key the feed now holds.
      const stored = new Set(feed.entries.flatMap((entry) => (
        entry.kind === 'chat-message' && entry.idempotencyKey && entry.id !== entry.idempotencyKey ? [entry.idempotencyKey] : [])));
      if (pending.some((entry) => stored.has(entry.id))) {
        forgetUnsent(this.scope(), stored);
        pending = pending.filter((entry) => !stored.has(entry.id));
      }
    }
    const failed = this.state.failed.has(key) ? new Set([...this.state.failed].filter((k) => k !== key)) : this.state.failed;
    this.set({ ...this.state, ...extra, pending, failed, feeds: { ...this.state.feeds, [key]: feed } });
  }

  private unreadFrom(secureUnread: number): void {
    this.unreadFromReads = true;
    this.secureUnread = secureUnread;
    const unread = this.counted();
    if (this.state.unread !== unread) this.state = { ...this.state, unread };
  }

  /** The header's count: the secure unread plus the Telegram arrivals Messages has not shown. */
  private counted(): number {
    return this.secureUnread + this.telegramArrivals.size;
  }

  private scope(): string {
    return `web:${this.userId ?? ''}`;
  }

  private runtime(key: FeedKey): FeedRuntime {
    let runtime = this.runtimes.get(key);
    if (!runtime) {
      runtime = { loading: false, busy: false, again: false, catchingUp: false, removedWhileOlder: null };
      this.runtimes.set(key, runtime);
    }
    return runtime;
  }

  private activeKeys(): FeedKey[] {
    return [...new Set([...BASE_KEYS, ...webFilterReads(this.filter).map((read) => feedKeyOf(read.query))])];
  }

  /** A filter's feeds: an unread one is read; one held from before catches up. */
  private activate(filter: WebMessageFilter): void {
    if (!this.state.live) return;
    for (const read of webFilterReads(filter)) {
      const key = feedKeyOf(read.query);
      const feed = this.state.feeds[key];
      if (!feed) {
        this.setFeed(key, emptyRoomFeed(read.room));
        void this.load(key);
      } else if (feed.loaded && !isBase(key)) {
        void this.refreshFeed(key);
      }
    }
  }

  /** The All feeds' entries are held: nothing they carry now is ever an arrival. */
  private hold(key: FeedKey): void {
    if (!isBase(key)) return;
    for (const entry of this.state.feeds[key]?.entries ?? []) this.held.add(entry.id);
  }

  private noteConversation(throughSeq: number): void {
    if (throughSeq <= 0 || this.conversationSeen) return;
    this.conversationSeen = true;
    // A stream opened before the conversation existed was never subscribed: reopen it.
    if (this.stream && this.streamBlind) this.reopenStream();
  }

  // ── Internals: reads ──

  private async load(key: FeedKey): Promise<void> {
    const runtime = this.runtime(key);
    if (runtime.loading) return;
    if (this.limited()) return; // the 429's catch-up loads it
    runtime.loading = true;
    const generation = this.generation;
    const query: ClientThreadQuery = queryOfKey(key);
    try {
      const response = await this.transport.fetchThread(query);
      const feed = this.state.feeds[key];
      if (generation !== this.generation || !feed) return;
      this.unreadFrom(response.secureUnread);
      this.setFeed(key, absorbFirstPage(feed, response));
      this.hold(key);
      this.noteConversation(response.throughSeq);
      await this.catchUp(key);
    } catch (error) {
      if (generation !== this.generation) return;
      if (isNotFound(error)) this.set({ ...this.state, notFound: new Set(this.state.notFound).add(key) });
      else if (!this.noteLimit(error)) this.set({ ...this.state, failed: new Set(this.state.failed).add(key) });
    } finally {
      runtime.loading = false;
      if (runtime.again && generation === this.generation) {
        runtime.again = false;
        void this.refreshFeed(key);
      }
    }
  }

  /** The feed's capped catch-up pages, until none remain; one that fails leaves the rest to the next refresh. */
  private async catchUp(key: FeedKey): Promise<void> {
    const runtime = this.runtime(key);
    if (runtime.catchingUp) return;
    runtime.catchingUp = true;
    const generation = this.generation;
    const query = queryOfKey(key);
    try {
      let feed = this.state.feeds[key];
      while (feed && feed.catchUpNext !== null && !this.limited()) {
        const response = await this.transport.fetchThread({ ...query, catchUpAfterSeq: feed.catchUpNext });
        const now = this.state.feeds[key];
        if (generation !== this.generation || !now) return;
        this.unreadFrom(response.secureUnread);
        feed = absorbCatchUpPage(now, response);
        this.setFeed(key, feed);
        this.hold(key);
      }
    } catch (error) {
      if (generation === this.generation) this.noteLimit(error);
    } finally {
      runtime.catchingUp = false;
    }
  }

  private async refreshFeed(key: FeedKey): Promise<void> {
    const feed = this.state.feeds[key];
    if (!feed) return;
    const runtime = this.runtime(key);
    if (!feed.loaded || runtime.busy) {
      if (runtime.loading || runtime.busy) runtime.again = true;
      return;
    }
    if (this.limited()) return;
    runtime.busy = true;
    const generation = this.generation;
    try {
      const query = queryOfKey(key);
      const since = feed.serverTime ? new Date(Date.parse(feed.serverTime) - LEDGER_OVERLAP_MS).toISOString() : undefined;
      const response = await this.transport.fetchThread({
        ...query,
        afterSeq: feed.throughSeq,
        ...(query.room === 'telegram' && since ? { ledgerSince: since } : {}),
      });
      const before = this.state.feeds[key];
      if (generation !== this.generation || !before) return;
      noteRemovals(runtime, response.ledger?.removals ?? []);
      this.unreadFrom(response.secureUnread);
      const after = absorbRefresh(before, response);
      const arrived = isBase(key) ? after.entries.filter((entry) => !this.held.has(entry.id)) : [];
      const studioTyped = arrived.some((entry) => entry.direction === 'outbound' && entry.channel === 'secure');
      if (after.room === 'telegram' && !this.panelOpen) {
        for (const entry of arrived) if (isStudioTelegramSend(entry)) this.telegramArrivals.add(entry.id);
        writeArrivals(this.scope(), this.telegramArrivals);
        this.state = { ...this.state, unread: this.counted() };
      }
      this.setFeed(key, after, studioTyped ? { typing: false } : {});
      this.hold(key);
      this.noteConversation(response.throughSeq);
      if (arrived.length > 0) {
        const items = arrived.map((entry): StreamItem => ({ room: after.room, entry }));
        for (const listener of this.arrivalListeners) listener(items);
      }
      // A catch-up cut short earlier carries on now.
      if (after.catchUpNext !== null) void this.catchUp(key);
    } catch (error) {
      this.noteLimit(error);
    } finally {
      runtime.busy = false;
      if (runtime.again && generation === this.generation) {
        runtime.again = false;
        void this.refreshFeed(key);
      }
    }
  }

  /** The one refresh every event, poll, reconnect and return to the tab runs. */
  private refreshAll(): void {
    for (const key of this.activeKeys()) void this.refreshFeed(key);
  }

  // ── Internals: rate limits ──

  private limited(): boolean {
    return Date.now() < this.limitedUntil;
  }

  /** True for a 429: reads pause until its Retry-After, then one catch-up runs. */
  private noteLimit(error: unknown): boolean {
    const ms = retryAfterMs(error);
    if (ms === null) return false;
    this.limitedUntil = Math.max(this.limitedUntil, Date.now() + ms);
    if (this.limitTimer) clearTimeout(this.limitTimer);
    const generation = this.generation;
    this.limitTimer = setTimeout(() => {
      this.limitTimer = null;
      if (generation !== this.generation) return;
      for (const key of this.activeKeys()) {
        const feed = this.state.feeds[key];
        if (feed && !feed.loaded) void this.load(key);
      }
      this.refreshAll();
      this.acknowledgeShown();
    }, Math.max(0, this.limitedUntil - Date.now()));
    return true;
  }

  // ── Internals: acknowledgements ──

  private acknowledgeShown(): void {
    if (!this.panelOpen || isHidden() || !this.state.live || this.limited() || this.readHold?.()) return;
    const reads = webFilterReads(this.filter);
    const feeds = reads.map((read) => this.state.feeds[feedKeyOf(read.query)]);
    if (feeds.some((feed) => !feed?.loaded)) return;
    const stream = mergeStream(feeds as RoomFeed[], webFilterKeeps(this.filter));
    for (const feed of feeds as RoomFeed[]) {
      const throughSeq = readToAcknowledge(stream, feed, { open: true, visible: true });
      if (throughSeq === null || this.ackInFlight.has(feed.room)) continue;
      this.ackInFlight.add(feed.room);
      const generation = this.generation;
      const room = feed.room;
      this.transport.markRead({ room, throughSeq }).then(
        (result) => {
          if (generation !== this.generation) return;
          this.ackInFlight.delete(room);
          const feedsNow = { ...this.state.feeds };
          for (const [key, held] of Object.entries(feedsNow) as [FeedKey, RoomFeed][]) {
            if (held.room === room) feedsNow[key] = markAcknowledged(held, throughSeq, result.secureUnread);
          }
          this.unreadFromReads = true;
          this.secureUnread = result.secureUnread;
          this.set({ ...this.state, feeds: feedsNow, unread: this.counted() });
        },
        (error) => {
          // Retried by the next fetch; never at once, so a failing POST cannot loop.
          if (generation === this.generation) this.ackInFlight.delete(room);
          this.noteLimit(error);
        },
      );
    }
  }

  // ── Internals: sends ──

  private async post(entry: ClientChatMessageEntry): Promise<void> {
    const key = entry.id;
    if (this.inFlight.has(key)) return;
    this.inFlight.add(key);
    const generation = this.generation;
    const scope = this.scope();
    this.setIssue(key, null);
    const body: ClientChatSendBody = { text: entry.payload.text, idempotencyKey: key };
    try {
      const response = await this.transport.send(body);
      if (generation !== this.generation) return;
      forgetUnsent(scope, new Set([key]));
      const secure = this.state.feeds.secure ?? emptyRoomFeed('secure');
      this.held.add(response.entry.id);
      this.setFeed('secure', absorbSent(secure, [response.entry]), {
        pending: this.state.pending.filter((item) => item.id !== key),
      });
      if (response.conversationCreated) this.noteConversation(Math.max(1, response.snapshotSeq));
    } catch (error) {
      // The failure line waits for the words to land in the bubble.
      await holdUntilLanded(key);
      if (generation !== this.generation) return;
      if (isRefusal(error)) {
        forgetUnsent(scope, new Set([key])); // the same request would be refused again
        this.setIssue(key, 'refused');
      } else {
        this.setIssue(key, 'unsent');
      }
    } finally {
      if (generation === this.generation) this.inFlight.delete(key);
    }
  }

  private setIssue(key: string, issue: SendIssue | null): void {
    const had = this.state.issues.get(key) ?? null;
    if (had === issue) return;
    const issues = new Map(this.state.issues);
    if (issue) issues.set(key, issue);
    else issues.delete(key);
    this.set({ ...this.state, issues });
  }

  // ── Internals: the stream ──

  private openStream(): void {
    if (this.stream || !this.state.live || isHidden()) return;
    const own = new AbortController();
    this.stream = own;
    this.streamBlind = !this.conversationSeen;
    const generation = this.generation;
    const current = () => generation === this.generation && this.stream === own;
    this.transport
      .readEvents(
        own.signal,
        () => {
          if (!current()) return;
          this.attempt = 0;
          this.setConnected(true);
        },
        (event) => {
          if (!current()) return;
          if (event.event === 'typing') {
            const typing = parseTyping(event.data);
            if (typing) this.showTyping(typing);
            return;
          }
          if (event.event === 'message' && this.holdsSeq(event.data)) return;
          // `ready` follows every open (the server writes it once subscribed, or at once with no
          // conversation): anything published before the subscription, an outage included, is read now.
          if (event.event === 'message' || event.event === 'ledger' || event.event === 'ready') this.refreshAll();
        },
      )
      .catch(() => undefined)
      .finally(() => {
        if (this.stream !== own) return; // closed or replaced on purpose
        this.stream = null;
        this.setConnected(false);
        this.scheduleRetry();
      });
  }

  /** A `message` event for a sequence a feed already holds (the client's own send) changes nothing. */
  private holdsSeq(data: string): boolean {
    let seq: unknown;
    try {
      seq = (JSON.parse(data) as { seq?: unknown }).seq;
    } catch {
      return false;
    }
    if (typeof seq !== 'number') return false;
    return Object.values(this.state.feeds).some((feed) => feed?.entries.some((entry) => seqOf(entry) === seq));
  }

  private closeStream(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    const own = this.stream;
    this.stream = null;
    own?.abort();
    this.setConnected(false);
  }

  private reopenStream(): void {
    this.closeStream();
    this.attempt = 0;
    this.openStream();
  }

  private scheduleRetry(): void {
    if (!this.state.live || isHidden()) return;
    const delay = STREAM_BACKOFF_MS[Math.min(this.attempt, STREAM_BACKOFF_MS.length - 1)];
    this.attempt += 1;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.openStream();
    }, delay);
  }

  private setConnected(connected: boolean): void {
    if (this.connected === connected) return;
    this.connected = connected;
    if (!connected && this.state.typing) this.set({ ...this.state, typing: false });
    this.schedulePoll();
  }

  /** The fallback poll; its deadline only ever moves earlier, so a flapping stream cannot postpone it. */
  private schedulePoll(): void {
    if (!this.state.live) return;
    const every = this.connected && !this.streamBlind ? POLL_STREAM_UP_MS : POLL_STREAM_DOWN_MS;
    const now = Date.now();
    this.pollDue = this.pollTimer ? Math.min(this.pollDue, now + every) : now + every;
    if (this.pollTimer) clearTimeout(this.pollTimer);
    const generation = this.generation;
    this.pollTimer = setTimeout(() => {
      this.pollTimer = null;
      if (generation !== this.generation) return;
      if (!isHidden()) this.refreshAll();
      this.schedulePoll();
    }, Math.max(0, this.pollDue - now));
  }

  private readonly onVisibility = (): void => {
    if (isHidden()) {
      if (this.hiddenTimer) clearTimeout(this.hiddenTimer);
      this.hiddenTimer = setTimeout(() => {
        this.hiddenTimer = null;
        this.closeStream();
      }, HIDDEN_CLOSE_MS);
      return;
    }
    if (this.hiddenTimer) clearTimeout(this.hiddenTimer);
    this.hiddenTimer = null;
    if (!this.stream) {
      this.attempt = 0;
      this.openStream();
    }
    this.refreshAll();
    this.acknowledgeShown();
  };

  private showTyping(event: ChatTypingEvent): void {
    // `until` is the server's clock; a device clock far off it falls back to the five seconds it means.
    const ms = typingLineMs(event.until);
    if (this.typingTimer) clearTimeout(this.typingTimer);
    this.typingTimer = setTimeout(() => {
      this.typingTimer = null;
      this.set({ ...this.state, typing: false });
    }, ms);
    if (!this.state.typing) this.set({ ...this.state, typing: true });
  }
}
